import * as Teams from '@microsoft/teams-js';
import * as React from 'react';

import provisioningErrorImg from '../../../assets/error-generic.png';
import { ApprovalRequestType } from '../../../data/types.generated';
import { EntryPoint, LogCategory, ScenarioName } from '../../../logger';
import {
    AgreementState,
    CreateEnvelopeInput,
    DeleteAdaptiveCardDocusignClient,
    DraftEnvelopeInput,
    SignApprovalType,
    WebhookBaseDataObject,
} from '../../../service/gen/esign';

import { useNotifySuccessInCreatRequest } from '../../../utilities/CustomHookUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ErrorPage } from '../../containers/ErrorPage';
import { LoadingPage } from '../../containers/LoadingPage';
import { createAdobeApproval } from '../CreateAdobeApproval/createAdobeApproval';
import { envelopeInputKey } from '../CreateAdobeApproval/CreateAdobeApproval.container';
import { createDocusignApproval } from '../CreateDocusignApproval/createDocusignApproval';
import { EsignIframeComponent } from '../EsignIframe/EsignIframe.component';

export interface CreateEsignApprovalChatCallbackProps {
    webhookDataObject: WebhookBaseDataObject;
}

// Creating an approval from chat triggers a route directly to this component.
export const CreateEsignApprovalChatCallback = (props: CreateEsignApprovalChatCallbackProps): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale().strings;
    const ecsConfig = host.getEcsConfig();
    const logger = host.getLogger();

    const { webhookDataObject } = props;

    const [loadingPage, setLoadingPage] = React.useState<boolean>(false);
    const [loadingPageMessage, setLoadingPageMessage] = React.useState<string>('');
    const [errorPage, setErrorPage] = React.useState<boolean>(false);
    const [errorPageMessage, setErrorPageMessage] = React.useState<string>(null);
    const [envelopeUrl, setEnvelopeUrl] = React.useState<string>(null);
    const [signApprovalType, setSignApprovalType] = React.useState<SignApprovalType>(null);

    const adobeStageViewEnabled = ecsConfig.MicrosoftTeamApprovals.AdobeStageViewEnabled;
    const docusignStageViewEnabled = ecsConfig.MicrosoftTeamApprovals.DocusignStageViewEnabled;

    // Notify success to teams sdk upon app reload from compose extension
    useNotifySuccessInCreatRequest(EntryPoint.ComposeExtension);

    const finalizeCreation = (launchStageView: boolean): void => {
        if (launchStageView) {
            Teams.dialog.update.resize({
                width: 'large' as Teams.TaskModuleDimension,
                height: 'large' as Teams.TaskModuleDimension,
            });
        } else {
            logger.flushLogs((): void => Teams.dialog.url.submit());
        }
    };

    React.useEffect((): void => {
        // If this prop exists, this component has been directly called by the task module continue response from task/submit.
        if (webhookDataObject != null) {
            // Gets the envelope input data from localStorage, previously stored before calling task.submit().
            const envelopeInput: CreateEnvelopeInput | DraftEnvelopeInput = JSON.parse(
                localStorage.getItem(envelopeInputKey)
            );
            if (envelopeInput == null) {
                logger.logTraceError(LogCategory.CreateDocusignApproval, 'Empty envelope in session storage');
                setErrorPageMessage(loc.error_default);
                setErrorPage(true);
                return;
            }
            envelopeInput.WebhookBaseDataObject = webhookDataObject;
            const signApprovalType = envelopeInput.SignApprovalType;
            setSignApprovalType(signApprovalType);
            if (signApprovalType === SignApprovalType.Adobe) {
                const adobeEnvelopeInput = envelopeInput as CreateEnvelopeInput;
                createAdobeApproval(
                    adobeEnvelopeInput,
                    setEnvelopeUrl,
                    setLoadingPage,
                    setLoadingPageMessage,
                    setErrorPage,
                    setErrorPageMessage
                ).then((): void => {
                    const isDraft = adobeEnvelopeInput.State === AgreementState.Authoring;
                    finalizeCreation(isDraft && adobeStageViewEnabled);
                });
            }
            if (signApprovalType === SignApprovalType.Docusign) {
                const docusignEnvelopeInput = envelopeInput as DraftEnvelopeInput;
                createDocusignApproval(
                    docusignEnvelopeInput,
                    setEnvelopeUrl,
                    setLoadingPage,
                    setLoadingPageMessage,
                    setErrorPage,
                    setErrorPageMessage
                ).then((): void => {
                    finalizeCreation(docusignEnvelopeInput.IsDraft && docusignStageViewEnabled);
                });
            }
            localStorage.removeItem(envelopeInputKey);
        }
    }, []);

    const onMessageProcessed = (success: boolean): void => {
        if (!success && webhookDataObject.MessageId != null && signApprovalType === SignApprovalType.Docusign) {
            const cardClient = new DeleteAdaptiveCardDocusignClient();
            cardClient.deleteAdaptiveCard(webhookDataObject);
        }
        logger.flushLogs((): void => Teams.dialog.url.submit());
    };

    return loadingPage ? (
        <LoadingPage message={loadingPageMessage} />
    ) : errorPage ? (
        <ErrorPage image={provisioningErrorImg} message={errorPageMessage} />
    ) : envelopeUrl != null && signApprovalType === SignApprovalType.Docusign && docusignStageViewEnabled ? (
        <EsignIframeComponent
            src={envelopeUrl}
            approvalType={ApprovalRequestType.Docusign}
            showHeader={true}
            onMessageProcessed={onMessageProcessed}
            scenarioName={ScenarioName.CreateEsignApproval}
        />
    ) : envelopeUrl != null && signApprovalType === SignApprovalType.Adobe && adobeStageViewEnabled ? (
        <EsignIframeComponent
            src={envelopeUrl}
            approvalType={ApprovalRequestType.ESign}
            showHeader={true}
            onMessageProcessed={onMessageProcessed}
            scenarioName={ScenarioName.CreateEsignApproval}
        />
    ) : null;
};
